import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { scriptApi } from '../lib/api'
import type { SaveScriptRequest } from '../types/script'

// ========== Scripts CRUD ==========

export function useScripts() {
  return useQuery({
    queryKey: ['scripts'],
    queryFn: async () => {
      const response = await scriptApi.getAll()
      return response.data
    },
  })
}

export function useScriptDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['scripts', id],
    queryFn: async () => {
      if (!id) throw new Error('Script ID is required')
      const response = await scriptApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateScript() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveScriptRequest) =>
      scriptApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scripts'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('scripts'),
      })
    },
  })
}

export function useUpdateScript(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveScriptRequest) =>
      scriptApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['scripts', id], data)
      queryClient.invalidateQueries({ queryKey: ['scripts'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('scripts'),
      })
    },
  })
}

export function useDeleteScript() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => scriptApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scripts'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('scripts'),
      })
    },
  })
}

// ========== Defaults ==========

export function useDefaultResultSettings() {
  return useQuery({
    queryKey: ['scripts', 'defaults', 'result-settings'],
    queryFn: async () => {
      const response = await scriptApi.getDefaultResultSettings()
      return response.data
    },
    staleTime: Infinity, // System defaults never change
  })
}

// ========== Pickers ==========

export function useTagPicker() {
  return useQuery({
    queryKey: ['scripts', 'picker', 'tags'],
    queryFn: async () => {
      const response = await scriptApi.getTagPickerOptions()
      return response.data
    },
  })
}

export function useQuestionPicker() {
  return useQuery({
    queryKey: ['scripts', 'picker', 'questions'],
    queryFn: async () => {
      const response = await scriptApi.getQuestionPickerOptions()
      return response.data
    },
  })
}

export function useQuestionAnswers(questionId: string | null | undefined) {
  return useQuery({
    queryKey: ['scripts', 'picker', 'questions', questionId, 'answers'],
    queryFn: async () => {
      if (!questionId) throw new Error('Question ID is required')
      const response = await scriptApi.getQuestionAnswers(questionId)
      return response.data
    },
    enabled: !!questionId,
  })
}

export function useAttributePicker() {
  return useQuery({
    queryKey: ['scripts', 'picker', 'attributes'],
    queryFn: async () => (await scriptApi.getAttributePicker()).data,
    staleTime: Infinity,
  })
}
